import { useEffect, useRef, useState } from "react";

/**
 * BuilderAdBanner — Google AdSense banner slot for the Resume Builder page.
 *
 * Behaviour:
 *  - If REACT_APP_ADSENSE_PUBLISHER_ID + REACT_APP_ADSENSE_BUILDER_SLOT are
 *    BOTH set → renders a live AdSense `<ins>` banner and calls
 *    `adsbygoogle.push({})` exactly once per mount.
 *  - If only publisher is set (no slot) → the site-wide Auto Ads script
 *    (loaded from `public/index.html`) will place ads automatically
 *    elsewhere on the page. We hide this section entirely so users don't
 *    see a stray "Advertisement" placeholder.
 *  - If neither is set (local dev) → shows a subtle dev placeholder so the
 *    layout is stable.
 *
 * The banner is excluded from PDF exports via a `@media print` rule in
 * index.css targeting `[data-testid="builder-ad-banner"]`.
 */
export default function BuilderAdBanner() {
  const publisherId = process.env.REACT_APP_ADSENSE_PUBLISHER_ID;
  const slotId = process.env.REACT_APP_ADSENSE_BUILDER_SLOT;
  const hasManualSlot = Boolean(publisherId && slotId);
  const hasPublisherOnly = Boolean(publisherId && !slotId);
  const containerRef = useRef(null);
  const initialisedRef = useRef(false);
  const [inView, setInView] = useState(false);

  // Lazy — wait until the container approaches the viewport before we
  // push the ad. Prevents impact on Builder editing performance.
  useEffect(() => {
    if (!containerRef.current || inView || !hasManualSlot) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setInView(true); },
      { rootMargin: "300px 0px" }
    );
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, [inView, hasManualSlot]);

  // Push the ad once the container is in (or near) view.
  useEffect(() => {
    if (!hasManualSlot || !inView || initialisedRef.current) return;
    initialisedRef.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("AdSense push failed:", e);
    }
  }, [hasManualSlot, inView]);

  // Auto Ads mode — script in index.html handles placement site-wide.
  // No visible slot needed here.
  if (hasPublisherOnly) return null;

  return (
    <section
      ref={containerRef}
      className="mt-8 md:mt-10 mb-2 w-full max-w-5xl mx-auto px-2 no-print"
      aria-label="Sponsored advertisement"
      data-testid="builder-ad-banner"
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 text-center mb-1.5">
        Sponsored
      </div>
      <div className="border border-black/10 rounded-sm bg-neutral-50/50 p-3 min-h-[110px] flex items-center justify-center">
        {hasManualSlot ? (
          <ins
            className="adsbygoogle"
            style={{ display: "block", width: "100%" }}
            data-ad-client={publisherId}
            data-ad-slot={slotId}
            data-ad-format="auto"
            data-full-width-responsive="true"
            data-testid="adsense-ins"
          />
        ) : (
          <div className="text-center text-neutral-400" data-testid="adsense-placeholder">
            <div className="text-xs font-mono uppercase tracking-widest">Advertisement</div>
            <div className="mt-1 text-[11px]">Google AdSense Banner · 728 × 90 (Responsive)</div>
          </div>
        )}
      </div>
    </section>
  );
}
